import { isEmpty, isNull, memoize } from 'lodash-es';
import { useCallback } from 'react';
import { useRouting } from './routing-context';

/**
 * Can be used as a drop-in replacement for React's useState function to read from and write state to a query param.
 */
export const useQueryParamState = <T>(queryParam: string, defaultValue: T) => {
  const { query, replaceWithAdditionalQueryParams, replaceWithoutQueryParams } = useRouting();
  const value = parseQueryParam(query[queryParam], defaultValue);

  const setValue = useCallback((newValue: T) => {
    if (isEmpty(newValue)) {
      replaceWithoutQueryParams([queryParam]);
    } else {
      replaceWithAdditionalQueryParams({ [queryParam]: JSON.stringify(newValue) });
    }
  }, [queryParam, replaceWithAdditionalQueryParams, replaceWithoutQueryParams]);

  return [
    value,
    setValue,
  ] as const;
};

const parseQueryParam = <T>(paramValue: string | null, defaultValue: T): T => {
  if (isNull(paramValue)) {
    return defaultValue;
  }

  try {
    return memoizedParseJson(paramValue);
  } catch (error) {
    return defaultValue;
  }
};

const memoizedParseJson = memoize(<T>(value: string) => JSON.parse(value) as T);
